// ─── SIGNUP INVITE SYSTEM ─────────────────────────────────────────────────────
// Gates *new tenant* creation during the trial — separate from the caregiver
// accessToken system in tokens.js, which gates access to an existing family.
// An invite code must be redeemed (email added to usedBy) before Firestore
// rules will allow a families/{familyId} create — see firestore.rules.
// The link embeds the code: https://yourapp.web.app/?invite=CODE
// On return visits (e.g. after the Google OAuth popup) the code is read from
// localStorage.

import { fsGet, fsUpdate } from './firebase';

const LOCAL_KEY = 'foh_invite_code';

// ─── INVITE SHAPE ─────────────────────────────────────────────────────────────
// signupInvites/{code}:
// {
//   code:      string (random hex, generated by the admin issuing script),
//   createdBy: string (admin email that issued it),
//   createdAt: ISO string,
//   expiresAt: ISO string | null,
//   maxUses:   number,
//   usedCount: number,
//   usedBy:    string[]  — emails that redeemed it,
//   active:    boolean,
//   label:     string | null,
// }

// ─── URL / LOCAL STORAGE ──────────────────────────────────────────────────────

export function getInviteCodeFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('invite') || null;
}

export function cleanInviteCodeFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('invite');
  window.history.replaceState({}, '', url.toString());
}

export function saveInviteCodeLocally(code) {
  try { localStorage.setItem(LOCAL_KEY, code); } catch {}
}

export function getSavedInviteCode() {
  try { return localStorage.getItem(LOCAL_KEY) || null; } catch { return null; }
}

export function clearSavedInviteCode() {
  try { localStorage.removeItem(LOCAL_KEY); } catch {}
}

function isInviteUsable(doc) {
  if (!doc.active) return false;
  if (doc.expiresAt && new Date(doc.expiresAt) < new Date()) return false;
  if ((doc.usedCount || 0) >= doc.maxUses) return false;
  return true;
}

// ─── REDEEM ───────────────────────────────────────────────────────────────────
// Adds the caller's email to usedBy and bumps usedCount by one. Idempotent
// for the same (code, email) pair — a reload mid-setup-wizard re-runs this,
// and since the email is already in usedBy that's treated as success without
// writing again (a maxUses:1 code would otherwise look "exhausted" to its own
// redeemer on the second pass). Firestore rules enforce the exact increment
// and reject if a *different* redeemer already used up the code, so a race
// between two people naturally resolves to one winner.

export async function redeemInviteCode(code, email) {
  const cleanEmail = email.toLowerCase();
  let doc;
  try {
    doc = await fsGet(`signupInvites/${code}`);
  } catch {
    return { ok: false, reason: 'error' };
  }
  if (!doc) return { ok: false, reason: 'not_found' };
  if ((doc.usedBy || []).includes(cleanEmail)) return { ok: true, alreadyRedeemed: true };
  if (!isInviteUsable(doc)) return { ok: false, reason: 'exhausted' };

  try {
    await fsUpdate(`signupInvites/${code}`, {
      usedCount: (doc.usedCount || 0) + 1,
      usedBy: [...(doc.usedBy || []), cleanEmail],
    });
    return { ok: true, alreadyRedeemed: false };
  } catch {
    return { ok: false, reason: 'race' };
  }
}
